import {
  createContext,
  PropsWithChildren,
  use,
  useCallback,
  useState,
} from "react";
import { v4 as uuid } from "uuid";
import { createView } from "lib/actions/views/createView";
import { mapViewFromResponse } from "lib/actions/views/mapViewFromResponse";
import { ViewModel } from "lib/types/views/ViewModel";
import { ViewCreateFormModel } from "lib/types/views/ViewCreateFormModel";
import {
  PendingViewModel,
  PendingViewStatus,
} from "lib/types/views/PendingViewModel";

type ViewsPendingCapturesContextValue = {
  addPendingCapture(
    projectId: number,
    model: Omit<ViewCreateFormModel, "projectId">,
    snapshotUploadPromise: Promise<void>,
  ): { pendingView: PendingViewModel; syncPromise: Promise<ViewModel> };
  getPendingViewsForProject(projectId: number): PendingViewModel[];
};

const ViewsPendingCapturesContext =
  createContext<ViewsPendingCapturesContextValue>(null);

export const useViewsPendingCaptures = () => use(ViewsPendingCapturesContext);

export function ViewsPendingCapturesProvider({ children }: PropsWithChildren) {
  const value = useProvider();
  return (
    <ViewsPendingCapturesContext value={value}>
      {children}
    </ViewsPendingCapturesContext>
  );
}

function useProvider(): ViewsPendingCapturesContextValue {
  const [entries, setEntries] = useState<Map<string, PendingViewModel>>(
    new Map(),
  );

  const updateEntryStatus = useCallback(
    (
      pendingId: string,
      status: PendingViewStatus,
      pendingError: string | null = null,
    ) => {
      setEntries((prev) => {
        const entry = prev.get(pendingId);
        if (!entry) return prev;
        return new Map(prev).set(pendingId, { ...entry, status, pendingError });
      });
    },
    [],
  );

  const removeEntry = useCallback((pendingId: string) => {
    setEntries((prev) => {
      const next = new Map(prev);
      next.delete(pendingId);
      return next;
    });
  }, []);

  const addPendingCapture = useCallback(
    (
      projectId: number,
      model: Omit<ViewCreateFormModel, "projectId">,
      snapshotUploadPromise: Promise<void>,
    ): { pendingView: PendingViewModel; syncPromise: Promise<ViewModel> } => {
      const pendingId = uuid();
      const pendingView: PendingViewModel = {
        pendingId,
        projectId,
        snapshotId: model.snapshotId,
        cameraPosition: model.cameraPosition ?? [],
        assetVersionIds: model.assetVersionIds ?? [],
        toolsStateJson: model.toolsStateJson ?? null,
        createdOn: new Date(),
        status: "pending",
        pendingError: null,
      };

      const syncPromise = (async () => {
        try {
          await snapshotUploadPromise;
          const response = await createView(projectId, { ...model, projectId });
          const serverView = mapViewFromResponse(response);
          removeEntry(pendingId);
          return serverView;
        } catch (err) {
          updateEntryStatus(
            pendingId,
            "failed",
            (err as Error)?.message ?? "Failed to sync",
          );
          throw err;
        }
      })();

      setEntries((prev) => new Map(prev).set(pendingId, pendingView));
      return { pendingView, syncPromise };
    },
    [updateEntryStatus, removeEntry],
  );

  const getPendingViewsForProject = useCallback(
    (projectId: number): PendingViewModel[] =>
      [...entries.values()].filter((entry) => entry.projectId === projectId),
    [entries],
  );

  return { addPendingCapture, getPendingViewsForProject };
}
